const dictionary = new WeakMap();
const fallbacks = new Map();

const VALUE = '_value';

function resolveLeaf(target, path) {
  const tail = path.reduce((branch, item) => {
    if (!branch.has(item)) branch.set(item, new Map());
    return branch.get(item);
  }, target);

  return {
    has: () => VALUE in tail,
    get: () => tail[VALUE],
    set: value => {
      tail[VALUE] = value;
    },
  };
}

/**
 * Аналог `Function.prototype.bind()` за исключением того что при вызове
 * с одними и теми же аргументами будет всегда возвращать один
 * и тот же результат.
 *
 * Это полезна при написании компонентов когда тому или иному обработчику нужно
 * забиндить какие-нибудь значения.
 *
 * Пример:
 * {
 *   render() {
 *     return (
 *       <ul>
 *         {items.map(item => (
 *           <ListItem onClick={bind(this.onClick, this, item)} />
 *         ))}
 *       </ul>
 *     );
 *   }
 * }
 *
 * В данном случае `shouldComponentUpdate` компонента `ListItem` будет
 * эффективно избегать перерисовок до тех пор пока содержимое `items` не будет
 * изменено в отличаи от обычного `bind` который будет каждый раз возвращать
 * новую функицию.
 */
export default function idempotentBind(handler, thisArg, ...args) {
  if (typeof handler !== 'function') {
    throw new TypeError('handler must be function');
  }

  let ctx;

  const type = typeof thisArg;

  if (type !== 'object' || type !== 'function' || thisArg === null) {
    if (!fallbacks.has(thisArg)) fallbacks.set(thisArg, {});
    ctx = fallbacks.get(thisArg);
  } else {
    ctx = thisArg;
  }

  dictionary.has(ctx) || dictionary.set(ctx, new Map());

  const tree = dictionary.get(ctx);
  const path = [handler, ...args];
  const leaf = resolveLeaf(tree, path);

  leaf.has() || leaf.set(handler.bind(thisArg, ...args));
  return leaf.get();
}
